import { Button, Progress, Typography, theme } from 'antd';
import React, { useEffect } from 'react';

import { config } from '@/config';
import {
  useTranscriptAnalysisStream,
  type AnalysisPhase,
} from '@/hooks/useTranscriptAnalysisStream';
import { SearchIcon } from '@/icons';

const { Title, Text } = Typography;

export interface AssessmentStep2GeneratingProps {
  assessmentInstanceId: string;
  clientId: string;
  projectId: string;
  accessToken: string | null;
  /** Override the stream URL (useful in tests). Derived from assessment params when omitted. */
  streamUrl?: string | null;
  clientLabel?: string;
  transcriptCount?: number;
  documentCount?: number;
  onNavigateReview?: () => void;
  onBack?: () => void;
}

/** Derive the SSE stream URL for the active transcript analysis job. */
export function buildStep2StreamUrl(
  clientId: string,
  projectId: string,
  assessmentInstanceId: string,
): string {
  return `${config.apiUrl}/clients/${clientId}/projects/${projectId}/assessments/${assessmentInstanceId}/step-2/stream`;
}

const PHASE_LABEL: Partial<Record<AnalysisPhase, string>> = {
  'analysis.loading': 'Loading transcript sources…',
  'analysis.scatter': 'Analyzing transcripts in parallel…',
  'analysis.gate': 'Checking analysis coverage…',
  'analysis.merging': 'Reconciling analysis batches…',
  'analysis.synthesizing': 'Reconciling analysis batches…',
  'analysis.validating': 'Validating analysis output…',
  'analysis.persisting': 'Preparing results for review…',
  'analysis.completed': 'Analysis ready for review.',
};

export const AssessmentStep2Generating: React.FC<AssessmentStep2GeneratingProps> = ({
  assessmentInstanceId,
  clientId,
  projectId,
  clientLabel,
  streamUrl,
  accessToken,
  transcriptCount = 0,
  documentCount = 0,
  onNavigateReview,
  onBack,
}) => {
  const { token } = theme.useToken();
  const resolvedStreamUrl =
    streamUrl ?? buildStep2StreamUrl(clientId, projectId, assessmentInstanceId);

  const { phase, progress, message, counts, isTerminal, hasError } = useTranscriptAnalysisStream({
    streamUrl: resolvedStreamUrl,
    accessToken,
  });

  useEffect(() => {
    if (isTerminal && !hasError && onNavigateReview) {
      onNavigateReview();
    }
  }, [isTerminal, hasError, onNavigateReview]);

  const isCancelled = phase === 'analysis.cancelled';
  const isCompleted = phase === 'analysis.completed';
  const phaseLabel = phase !== null ? (PHASE_LABEL[phase] ?? null) : null;

  if (hasError) {
    const iconColor = isCancelled ? 'rgba(128,128,128,0.55)' : 'var(--color-error, #ef4444)';
    const title = isCancelled ? 'Analysis Cancelled' : 'Analysis Failed';
    const body = isCancelled
      ? 'Analysis was stopped before completion. Return to Step 2 to start a new run.'
      : 'Analysis could not complete after multiple retries. Review source documents and start a new run.';

    return (
      <div className="step-generating__centered">
        <div className="step-generating__icon-ring" style={{ border: `2px solid ${iconColor}` }}>
          <SearchIcon style={{ fontSize: 32, color: iconColor }} />
        </div>
        <Title level={3}>{title}</Title>
        <Text type="secondary" className="step-generating__subtitle">
          {body}
        </Text>
        <Progress
          percent={progress}
          status="exception"
          showInfo={false}
          style={{ maxWidth: 400, width: '100%' }}
        />
        {message && (
          <Text type="secondary" className="step-generating__phase-label">
            {message}
          </Text>
        )}
        <Text type="secondary" className="step-generating__phase-label">
          {transcriptCount} transcripts &nbsp;·&nbsp; {documentCount} client documents
        </Text>
        {!isCancelled && onBack && <Button onClick={onBack}>Back to Step 2</Button>}
      </div>
    );
  }

  const transcriptPlural = transcriptCount !== 1 ? 's' : '';
  const documentPlural = documentCount !== 1 ? 's' : '';
  const documentPhrase =
    documentCount > 0 ? ` and ${documentCount} client document${documentPlural}` : '';
  const subtitleCounts =
    transcriptCount > 0
      ? `${transcriptCount} interview transcript${transcriptPlural}${documentPhrase}`
      : null;
  const clientPhrase = clientLabel ? ` for ${clientLabel}` : '';

  const batchProgress =
    counts !== null ? `${counts.completed} of ${counts.total} batches processed` : null;

  return (
    <div className="step-generating__centered">
      <div className="step-generating__icon-ring">
        <SearchIcon style={{ fontSize: 32, color: token.colorPrimary }} />
      </div>
      <Title level={3}>Analyzing Transcripts &amp; Documents</Title>
      <Text type="secondary" className="step-generating__subtitle">
        {subtitleCounts
          ? `Blend AI is cross-referencing ${subtitleCounts} to synthesize themes, signals, and maturity indicators${clientPhrase}.`
          : `Blend AI is analyzing your interview transcripts and building the synthesis report${clientPhrase}.`}
      </Text>
      <Progress
        percent={progress}
        status={isCompleted ? 'success' : 'active'}
        strokeColor={isCompleted ? undefined : token.colorPrimary}
        showInfo={false}
        style={{ maxWidth: 400, width: '100%' }}
      />
      {phaseLabel !== null && !isCompleted && (
        <Text type="secondary" className="step-generating__phase-label">
          {phaseLabel}
        </Text>
      )}
      {transcriptCount > 0 && (
        <Text type="secondary" className="step-generating__phase-label">
          {transcriptCount} transcript{transcriptPlural}
          {documentCount > 0 ? ` · ${documentCount} document${documentPlural}` : ''}
          {batchProgress ? ` · ${batchProgress}` : ''}
        </Text>
      )}
    </div>
  );
};

export default AssessmentStep2Generating;
